import { useState, useMemo } from 'react'
import { GraduationCap, Search, Plus, UserX, CheckCircle2, Clock, AlertCircle } from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useTrainingSessions,
  useTrainingVocabularies,
  useTrainers,
  useUpdateTrainingSession,
} from '@/hooks/use-api'
import { useUser } from '@/contexts/UserContext'
import { useToast } from '@/hooks/use-toast'
import { useDebounce } from '@/hooks/use-debounce'
import { usePersistentState, makeFilterKey } from '@/hooks/use-persistent-state'
import { explainError } from '@/lib/explain-error'
import { GRACKLE_COLORS } from '@/lib/theme'
import { AccountAliasResolver } from '@/components/AccountAliasResolver'
import {
  ShowCompletedToggle,
  CompletedHiddenNote,
  NothingOutstanding,
} from '@/components/training/SheetTable'
import { NewTrainingSessionDialog } from '@/components/NewTrainingSessionDialog'
import type { TrainingSession } from '@/types'

/**
 * The training worklist — a faithful replacement for the "Training Tracking" sheet.
 *
 * All fourteen of the sheet's columns are here. An earlier version showed six,
 * which hid four columns that are over 90% populated in the sheet (post meeting
 * 248/248, recording needed 247, deck sent 244, pre meeting 225) and omitted Notes
 * entirely — load-bearing, since one of the training types is literally
 * "Custom - Explain in Notes".
 *
 * Deliberately a worklist and not a calendar: the sheet has no scheduled-date
 * column and neither does the CRM. Its only date is "Training Completed On".
 */

/**
 * Row tints, from the Grackle V2 palette (lib/theme.ts).
 *
 * NOT the sheet's scheme, and now knowingly so. Reading the .xlsx shows the workbook
 * has exactly one conditional-format rule: fill #B7E1CD on a CELL when it is
 * non-blank, applied to Sales Rep, Trainer and Recording link. It marks "somebody has
 * filled this in", not a stage of delivery. This tints whole ROWS by status instead,
 * which answers a different question. Reconciling the two is an open decision.
 *
 * Pistachio and Pollen are fills-with-dark-text only, per theme.ts, which is how they
 * are used: a translucent tint under Prussian Blue text.
 */
const STATUS_STYLE: Record<string, { bg: string; label: string }> = {
  Completed: { bg: GRACKLE_COLORS.pistachio, label: 'Completed' },
  'In progress': { bg: GRACKLE_COLORS.pollen, label: 'In progress' },
  'Not started': { bg: GRACKLE_COLORS.powderBlue, label: 'Not started' },
  // Blank is a real state — the sheet leaves the cell empty when nobody has said
  // yet, and 48 records are in that position. It was previously stored as the
  // invented status "Unspecified"; migration 126 put it back to NULL.
  '': { bg: GRACKLE_COLORS.paleGrey, label: 'Not set' },
}

type ColKind = 'text' | 'select' | 'date' | 'longtext' | 'readonly'

interface Column {
  key: string
  label: string
  kind: ColKind
  vocab?: 'status' | 'courses' | 'yes_no' | 'billed_free' | 'duration' | 'trainer'
  width: string
  /** A qualification the sheet's own header carried — kept rather than dropped. */
  note?: string
}

/** Every column, in the sheet's order, so the two can be read side by side. */
const COLUMNS: Column[] = [
  { key: 'account_name', label: 'Client', kind: 'readonly', width: 'min-w-[15rem]' },
  {
    key: 'course_name',
    label: 'Training Type',
    kind: 'select',
    vocab: 'courses',
    width: 'min-w-[16rem]',
  },
  {
    key: 'duration_text',
    label: 'Duration',
    kind: 'select',
    vocab: 'duration',
    width: 'min-w-[9rem]',
  },
  {
    key: 'billed_free',
    label: 'Billed/Free',
    kind: 'select',
    vocab: 'billed_free',
    width: 'min-w-[8rem]',
  },
  {
    key: 'pre_meeting_required',
    label: '30-min pre meeting',
    kind: 'select',
    vocab: 'yes_no',
    width: 'min-w-[8.5rem]',
  },
  {
    key: 'post_meeting_required',
    label: '30-min post meeting',
    kind: 'select',
    vocab: 'yes_no',
    width: 'min-w-[8.5rem]',
    note: 'GracklePDF only',
  },
  {
    key: 'recording_needed',
    label: 'Recording needed',
    kind: 'select',
    vocab: 'yes_no',
    width: 'min-w-[8.5rem]',
  },
  { key: 'sales_rep_name', label: 'Sales Rep', kind: 'text', width: 'min-w-[11rem]' },
  {
    key: 'trainer_name',
    label: 'Trainer',
    kind: 'select',
    vocab: 'trainer',
    width: 'min-w-[12rem]',
  },
  { key: 'status', label: 'Status', kind: 'select', vocab: 'status', width: 'min-w-[10rem]' },
  {
    key: 'deck_sent',
    label: 'Accessible deck sent',
    kind: 'select',
    vocab: 'yes_no',
    width: 'min-w-[8.5rem]',
  },
  { key: 'completed_on', label: 'Completed on', kind: 'date', width: 'min-w-[9.5rem]' },
  { key: 'recording_url', label: 'Recording link', kind: 'text', width: 'min-w-[12rem]' },
  { key: 'notes', label: 'Notes', kind: 'longtext', width: 'min-w-[22rem]' },
]

/** Fields a trainer may change on their own rows. The API enforces the same list. */
const TRAINER_FIELDS = new Set(['status', 'completed_on', 'recording_url', 'notes', 'deck_sent'])

export default function TrainingSessions() {
  const { currentUser, userType } = useUser()
  const { toast } = useToast()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const [status, setStatus] = useState('all')
  const [mine, setMine] = useState(userType === 'Training')
  const [newOpen, setNewOpen] = useState(false)

  // Shared with the five worksheet screens, so the preference follows the user through
  // the whole Training section rather than needing to be set on each tab.
  const fk = makeFilterKey(currentUser?.id, 'training')
  const [showCompleted, setShowCompleted] = usePersistentState(fk('showCompleted'), false)

  const { data, isLoading } = useTrainingSessions({
    search: debouncedSearch || undefined,
    status: status === 'all' ? undefined : status,
    mine: mine || undefined,
    // Ignored by the server whenever an explicit status is set, so picking "Completed"
    // from the dropdown works without touching the toggle.
    include_completed: showCompleted || undefined,
    limit: 500,
  })
  const { data: vocabResp } = useTrainingVocabularies()
  const { data: trainerResp } = useTrainers()
  const updateSession = useUpdateTrainingSession()

  const sessions = data?.data ?? []
  const stats = data?.stats
  const vocab = vocabResp?.data
  const trainerNames = useMemo(() => (trainerResp?.data ?? []).map((t) => t.name), [trainerResp])

  /**
   * How many completed sessions this view is keeping out of the way.
   *
   * The stats aggregate is unfiltered by design — the four cards describe the whole
   * worklist, not the current filters — so its Completed count only equals what is
   * being hidden when nothing else is narrowing the list. With a search or "My
   * sessions" active it would overstate things, so it reports zero rather than a
   * number that does not add up, and the note simply does not render.
   *
   * Also zero when an explicit status is chosen, because the server honours that in
   * preference to the exclusion.
   */
  const completedHidden =
    !showCompleted && status === 'all' && !debouncedSearch && !mine ? (stats?.completed ?? 0) : 0

  const isTrainer = userType === 'Training'
  const canAssign = ['Super Admin', 'Admin', 'User'].includes(userType || '')

  const canEditCell = (s: TrainingSession, key: string) => {
    if (canAssign) return true
    if (!isTrainer) return false
    return s.trainer_id === currentUser?.id && TRAINER_FIELDS.has(key)
  }

  const optionsFor = (c: Column): string[] => {
    if (c.vocab === 'trainer') return trainerNames
    if (!c.vocab || !vocab) return []
    return vocab[c.vocab] ?? []
  }

  const save = async (s: TrainingSession, key: string, value: unknown) => {
    try {
      await updateSession.mutateAsync({ id: s.id, data: { [key]: value } })
    } catch (err) {
      toast({
        title: 'Could not save that change',
        description: explainError(err, 'Please try again.'),
        variant: 'destructive',
      })
    }
  }

  /** 'N-A' is what is stored; 'N/A' is what people read. */
  const display = (v: unknown) => (v === 'N-A' ? 'N/A' : String(v ?? ''))

  const renderCell = (s: TrainingSession, c: Column) => {
    const raw = (s as unknown as Record<string, unknown>)[c.key]
    const editable = canEditCell(s, c.key)

    if (c.kind === 'readonly' || !editable) {
      if (c.key === 'trainer_name' && !raw) {
        return <span style={{ color: GRACKLE_COLORS.darkRaspberry }}>Unassigned</span>
      }
      if (c.key === 'recording_url' && raw) {
        return (
          <a href={String(raw)} target="_blank" rel="noreferrer" className="underline">
            Recording
          </a>
        )
      }
      return (
        <span className={c.kind === 'longtext' ? 'whitespace-pre-wrap' : ''}>
          {display(raw) || '—'}
        </span>
      )
    }

    if (c.kind === 'select') {
      // Always offer what is stored, even when the vocabulary has not heard of it —
      // otherwise a Radix Select falls back to its placeholder and the value looks
      // blank. The trainer list in particular is names, not a closed set.
      const options = optionsFor(c)
      const current = typeof raw === 'string' ? raw : ''
      const withCurrent = current && !options.includes(current) ? [...options, current] : options

      return (
        <Select value={current} onValueChange={(v) => save(s, c.key, v === '__clear' ? null : v)}>
          <SelectTrigger
            className="h-8 w-full bg-white/70"
            aria-label={`${c.label} for ${s.course_name} at ${s.account_name ?? 'this client'}`}
          >
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__clear">—</SelectItem>
            {withCurrent.map((o) => (
              <SelectItem key={o} value={o}>
                {display(o)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    }

    if (c.kind === 'date') {
      return (
        <Input
          type="date"
          value={(raw as string) ?? ''}
          onChange={(e) => save(s, c.key, e.target.value || null)}
          className="h-8 bg-white/70"
          aria-label={`${c.label} for ${s.course_name}`}
        />
      )
    }

    return (
      <Input
        defaultValue={(raw as string) ?? ''}
        onBlur={(e) => {
          if (e.target.value !== ((raw as string) ?? '')) {
            save(s, c.key, e.target.value || null)
          }
        }}
        className="h-8 bg-white/70"
        aria-label={`${c.label} for ${s.course_name}`}
      />
    )
  }

  return (
    <main className="space-y-4 p-4 md:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <GraduationCap className="h-6 w-6" aria-hidden="true" />
            Training worklist
          </h1>
          <p className="text-sm text-muted-foreground">
            Every column from the tracking sheet. Sessions appear automatically when a deal closes;
            assign a trainer and record progress as it is delivered.
          </p>
        </div>
        {canAssign && (
          <Button onClick={() => setNewOpen(true)} aria-label="Add a training session">
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            New session
          </Button>
        )}
      </header>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          {
            label: 'Unassigned',
            value: stats?.unassigned,
            icon: UserX,
            bg: GRACKLE_COLORS.pollen,
          },
          {
            label: 'Not started',
            value: stats?.notStarted,
            icon: AlertCircle,
            bg: GRACKLE_COLORS.powderBlue,
          },
          {
            label: 'In progress',
            value: stats?.inProgress,
            icon: Clock,
            bg: GRACKLE_COLORS.paleBlue,
          },
          {
            label: 'Completed',
            value: stats?.completed,
            icon: CheckCircle2,
            bg: GRACKLE_COLORS.pistachio,
          },
        ].map(({ label, value, icon: Icon, bg }) => (
          <Card key={label} style={{ backgroundColor: bg, borderColor: bg }}>
            <CardContent className="flex items-center justify-between p-3">
              <div style={{ color: GRACKLE_COLORS.prussianBlue }}>
                <div className="text-xs font-medium">{label}</div>
                <div className="text-xl font-bold leading-none">{value ?? '—'}</div>
              </div>
              <Icon
                className="h-4 w-4"
                style={{ color: GRACKLE_COLORS.prussianBlue }}
                aria-hidden="true"
              />
            </CardContent>
          </Card>
        ))}
      </div>

      {canAssign && <AccountAliasResolver source="training-tracker" />}

      <Card>
        <CardHeader className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-[14rem] flex-1">
              <Search
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search client, course or notes..."
                className="pl-9"
                aria-label="Search training sessions"
              />
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="status-filter" className="text-sm text-muted-foreground">
                Status
              </label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger id="status-filter" className="w-[11rem]">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {(vocab?.status ?? []).map((sv) => (
                    <SelectItem key={sv} value={sv}>
                      {sv}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant={mine ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMine((v) => !v)}
              aria-pressed={mine}
            >
              My sessions
            </Button>
            <ShowCompletedToggle
              showCompleted={showCompleted}
              onToggle={() => setShowCompleted((v) => !v)}
            />
            <span className="text-xs text-muted-foreground">{sessions.length} shown</span>
            <CompletedHiddenNote hiddenCount={completedHidden} />
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {isLoading ? (
            <p className="py-8 text-center text-muted-foreground" role="status">
              Loading the worklist...
            </p>
          ) : sessions.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              {completedHidden > 0 ? (
                <NothingOutstanding
                  hiddenCount={completedHidden}
                  onShow={() => setShowCompleted(true)}
                />
              ) : mine ? (
                'Nothing is assigned to you yet.'
              ) : (
                'No sessions match those filters.'
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <caption className="sr-only">
                  Training sessions with every column from the tracking sheet. Rows are tinted by
                  status; the colour key is below the table.
                </caption>
                <thead>
                  <tr style={{ backgroundColor: GRACKLE_COLORS.prussianBlue, color: '#FFFFFF' }}>
                    {COLUMNS.map((c) => (
                      <th
                        key={c.key}
                        scope="col"
                        className={`p-2 text-left align-bottom text-xs font-semibold ${c.width}`}
                      >
                        {c.label}
                        {c.note && <span className="block font-normal opacity-80">({c.note})</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => {
                    const tint = STATUS_STYLE[s.status ?? '']
                    return (
                      <tr
                        key={s.id}
                        className="border-b align-top"
                        style={{
                          // Translucent so the tint reads as a highlight rather than
                          // fighting the text, which stays Prussian Blue for contrast.
                          backgroundColor: tint ? `${tint.bg}55` : undefined,
                          color: GRACKLE_COLORS.prussianBlue,
                        }}
                      >
                        {COLUMNS.map((c) => (
                          <td key={c.key} className="p-1.5">
                            {renderCell(s, c)}
                          </td>
                        ))}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span className="font-medium">Row colour</span>
        {Object.entries(STATUS_STYLE).map(([key, v]) => (
          <span key={key} className="flex items-center gap-1.5">
            <span
              className="inline-block h-3 w-3 rounded-sm border border-black/20"
              style={{ backgroundColor: v.bg }}
              aria-hidden="true"
            />
            {v.label}
          </span>
        ))}
        <Badge variant="outline">Grackle brand palette</Badge>
      </div>

      <NewTrainingSessionDialog open={newOpen} onOpenChange={setNewOpen} />
    </main>
  )
}
